import { Badge, Button, Card, EmptyState, Input, Spinner, useToast } from '@ohear/ui'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { AdminGuard } from '../../features/admin/AdminGuard'
import { AdminNav } from '../../features/admin/AdminNav'
import {
  type AdminClientError,
  type ClientErrorFilters,
  useClientErrors,
  useResolveClientError,
} from '../../lib/api/admin'

export const Route = createFileRoute('/admin/client-errors')({
  component: ClientErrorsPage,
})

const PAGE_SIZE = 50

function ClientErrorsPage() {
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<ClientErrorFilters>({ unresolved: true })
  const [draft, setDraft] = useState<ClientErrorFilters>({ unresolved: true })

  const fullFilters: ClientErrorFilters = { page, page_size: PAGE_SIZE, ...filters }
  const errs = useClientErrors(fullFilters)
  const resolve = useResolveClientError()
  const toast = useToast()

  const items = errs.data?.items ?? []
  const total = errs.data?.total ?? 0
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const apply = (e: React.FormEvent) => {
    e.preventDefault()
    setFilters({
      released_version: draft.released_version || undefined,
      route: draft.route || undefined,
      since: draft.since || undefined,
      until: draft.until || undefined,
      unresolved: draft.unresolved ?? false,
    })
    setPage(1)
  }

  const clear = () => {
    setDraft({ unresolved: true })
    setFilters({ unresolved: true })
    setPage(1)
  }

  const onResolve = (row: AdminClientError) => {
    resolve.mutate(row.id, {
      onSuccess: () => toast.show({ severity: 'success', title: 'Marked resolved' }),
      onError: (err) =>
        toast.show({
          severity: 'error',
          title: 'Resolve failed',
          description: err instanceof Error ? err.message : 'Unknown error',
        }),
    })
  }

  return (
    <AdminGuard>
      <section className="flex flex-col gap-3" aria-labelledby="admin-client-errors-title">
        <header className="flex flex-col gap-1">
          <div className="text-[12px] uppercase tracking-wide text-[var(--text-3)]">
            Platform admin
          </div>
          <h1
            id="admin-client-errors-title"
            className="text-[22px] font-semibold tracking-tight m-0"
          >
            Client errors
          </h1>
          <p className="text-[13px] text-[var(--text-2)] m-0">
            Browser-side exceptions reported by the SPA. Filter by release, route, or time. Mark a
            row resolved once you've fixed the underlying bug.
          </p>
        </header>

        <AdminNav />

        <Card>
          <form className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" onSubmit={apply}>
            <label className="flex flex-col gap-1 text-[13px]">
              <span className="font-medium">Release version</span>
              <Input
                value={draft.released_version ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, released_version: e.target.value }))}
                placeholder="e.g. 0.4.2"
              />
            </label>
            <label className="flex flex-col gap-1 text-[13px]">
              <span className="font-medium">Route</span>
              <Input
                value={draft.route ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, route: e.target.value }))}
                placeholder="e.g. /actor/sessions"
              />
            </label>
            <label className="flex items-center gap-2 text-[13px] sm:col-span-2 lg:col-span-1">
              <input
                type="checkbox"
                checked={draft.unresolved ?? false}
                onChange={(e) => setDraft((d) => ({ ...d, unresolved: e.target.checked }))}
              />
              Unresolved only
            </label>
            <label className="flex flex-col gap-1 text-[13px]">
              <span className="font-medium">Since</span>
              <Input
                type="datetime-local"
                value={draft.since ?? ''}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    since: e.target.value ? new Date(e.target.value).toISOString() : undefined,
                  }))
                }
              />
            </label>
            <label className="flex flex-col gap-1 text-[13px]">
              <span className="font-medium">Until</span>
              <Input
                type="datetime-local"
                value={draft.until ?? ''}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    until: e.target.value ? new Date(e.target.value).toISOString() : undefined,
                  }))
                }
              />
            </label>
            <div className="flex items-end gap-2">
              <Button type="submit">Apply</Button>
              <Button type="button" variant="ghost" onClick={clear}>
                Clear
              </Button>
            </div>
          </form>
        </Card>

        <Card dense>
          {errs.isLoading ? (
            <div className="flex items-center gap-2 text-[var(--text-2)]">
              <Spinner /> Loading client errors...
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              title="No client errors"
              description="Nothing matched your filter — either we're shipping clean code or your window is too narrow."
            />
          ) : (
            <div className="overflow-x-auto -mx-3 -my-3">
              <table
                className="w-full text-[13px] border-separate"
                style={{ borderSpacing: 0 }}
                aria-label="Client errors"
              >
                <thead
                  className="sticky top-0 z-10 text-left text-[11px] uppercase tracking-wide text-[var(--text-2)]"
                  style={{ background: 'var(--bg-3)' }}
                >
                  <tr>
                    <th className="px-3 py-2 font-medium">When</th>
                    <th className="px-3 py-2 font-medium">Release</th>
                    <th className="px-3 py-2 font-medium">Route</th>
                    <th className="px-3 py-2 font-medium">Message</th>
                    <th className="px-3 py-2 font-medium">User</th>
                    <th className="px-3 py-2 font-medium">State</th>
                    <th className="px-3 py-2 font-medium" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.id} className="align-top transition-colors hover:bg-[var(--bg-3)]">
                      <td
                        className="px-3 py-2 text-[var(--text-2)] whitespace-nowrap tabular-nums border-t"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        {new Date(row.created_at).toLocaleString()}
                      </td>
                      <td
                        className="px-3 py-2 font-mono text-[12px] border-t"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        {row.released_version ?? '—'}
                      </td>
                      <td
                        className="px-3 py-2 text-[12px] border-t max-w-[180px] truncate"
                        style={{ borderColor: 'var(--border)' }}
                        title={row.route ?? undefined}
                      >
                        {row.route ?? '—'}
                      </td>
                      <td
                        className="px-3 py-2 text-[12px] text-[var(--text-2)] max-w-[420px] border-t"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        <code className="break-all whitespace-pre-wrap">{row.error_message}</code>
                        {row.error_stack ? (
                          <details className="mt-1">
                            <summary className="cursor-pointer text-[11px] text-[var(--text-3)]">
                              stack
                            </summary>
                            <pre className="mt-1 text-[11px] whitespace-pre-wrap break-all text-[var(--text-3)]">
                              {row.error_stack}
                            </pre>
                          </details>
                        ) : null}
                      </td>
                      <td
                        className="px-3 py-2 font-mono text-[12px] text-[var(--text-2)] border-t"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        {row.user_id ? row.user_id.slice(0, 8) : '—'}
                      </td>
                      <td className="px-3 py-2 border-t" style={{ borderColor: 'var(--border)' }}>
                        {row.resolved_at ? (
                          <Badge variant="green">resolved</Badge>
                        ) : (
                          <Badge variant="amber">open</Badge>
                        )}
                      </td>
                      <td
                        className="px-3 py-2 text-right whitespace-nowrap border-t"
                        style={{ borderColor: 'var(--border)' }}
                      >
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onResolve(row)}
                          loading={resolve.isPending && resolve.variables === row.id}
                          disabled={!!row.resolved_at}
                        >
                          Resolve
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div className="flex items-center justify-between text-[12px] text-[var(--text-2)]">
          <span>
            Page {page} of {lastPage} - {total} total
          </span>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="ghost"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={page >= lastPage}
              onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      </section>
    </AdminGuard>
  )
}
